import { useDeferredValue, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Phone, Plus, Search, Smartphone, Trash2, Upload, X } from 'lucide-react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../../__generated__/schema-lite';
import { useCurrentAccount } from '../../account/AccountContext';
import { useCan } from '../../account/capabilities';
import { translateError } from '../../lib/translate-error';

/** Who the callers are — so a call log reads as people instead of numbers.
 *
 *  The phone number IS the identity here: `@@unique([accountId, e164])`, and it is what
 *  an incoming call is matched against. That drives two decisions below — the number is
 *  the only required field (a caller you have not named yet is still worth recording),
 *  and it is normalised before saving, because "0888 123 456" and "+359888123456" must
 *  not become two contacts for one person.
 *
 *  Reading is every member's; writing is anyone but a VIEWER, mirroring the policy
 *  (`role != 'VIEWER'`) through the `workCalls` capability. Contacts are part of working
 *  the calls, not configuration — a responder maintains them. */
type ContactRow = {
  id: string;
  e164: string;
  name: string | null;
  relationshipTag: string | null;
  notes: string | null;
  callMessage: string | null;
  callMessageExpiresAt: string | null;
};

type DurationUnit = 'hours' | 'days';
type Draft = {
  id: string | null;
  e164: string;
  name: string;
  relationshipTag: string;
  notes: string;
  callMessage: string;
  durationAmount: string;
  durationUnit: DurationUnit;
};

const EMPTY: Draft = {
  id: null,
  e164: '',
  name: '',
  relationshipTag: '',
  notes: '',
  callMessage: '',
  durationAmount: '1',
  durationUnit: 'hours',
};

/** Max amount per unit — hours cap at a day, days at a month. */
const DURATION_MAX: Record<DurationUnit, number> = { hours: 24, days: 30 };

/** Strip the punctuation people type and keep the E.164 shape.
 *
 *  Deliberately does NOT guess a country: turning a leading 0 into +359 would be right
 *  in Sofia and wrong for the first customer abroad, and a silently wrong number is a
 *  contact that never matches a call. The form asks for the country code instead. */
function normalisePhone(raw: string): string {
  return raw.replace(/[\s()\-.–—]/g, '');
}

const E164 = /^\+[1-9]\d{6,14}$/;

// Contact Picker API — Android Chromium only (iOS Safari and desktop don't have it),
// so feature-detecting it is also the "show on Android" test. Not in TS's DOM lib.
type PickedContact = { name?: string[]; tel?: string[] };
type ContactsManager = {
  select(props: string[], opts?: { multiple?: boolean }): Promise<PickedContact[]>;
};
function getContactsManager(): ContactsManager | null {
  if (typeof window === 'undefined' || !('ContactsManager' in window)) return null;
  const nav = navigator as unknown as { contacts?: ContactsManager };
  return nav.contacts ?? null;
}

/** Minimal vCard parser: one entry per BEGIN/END VCARD block, taking FN as the
 *  name and the first TEL as the number. Enough for a phone's exported .vcf (the
 *  cross-platform import that also works on iOS); not a full RFC 6350 parser. */
function parseVcards(text: string): Array<{ name: string | null; tel: string }> {
  const out: Array<{ name: string | null; tel: string }> = [];
  for (const block of text.split(/BEGIN:VCARD/i).slice(1)) {
    let name: string | null = null;
    let tel = '';
    for (const line of block.split(/\r?\n/)) {
      if (/^END:VCARD/i.test(line)) break;
      const colon = line.indexOf(':');
      if (colon < 0) continue;
      // Property name is before the first ':' and may carry ;params (TEL;TYPE=CELL).
      const prop = (line.slice(0, colon).split(';')[0] ?? '').trim().toUpperCase();
      const value = line.slice(colon + 1).trim();
      if (prop === 'FN' && value) name = value;
      else if (prop === 'TEL' && !tel) tel = value.replace(/^tel:/i, '');
    }
    if (tel) out.push({ name, tel });
  }
  return out;
}

export default function ContactsPage() {
  const { t } = useTranslation();
  const { accountId } = useCurrentAccount();
  const can = useCan();
  const mayEdit = can('workCalls');
  // Present only where the Contact Picker API exists (Android Chromium) — the
  // import button is hidden everywhere else (iOS, desktop).
  const contactsApi = getContactsManager();
  // A VIEWER gets `e164` back as null (field rule in schema.zmodel), so the number is
  // neither shown nor worth searching on — including it in the OR would only produce
  // "nobody matches that" for a term the server can never match.
  const seesNumbers = can('seeContactNumbers');
  const crud = useClientQueries(schema);

  const fileRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  // Typing should not fire a request per keystroke; React holds the previous list while
  // the new one loads instead of blanking the screen.
  const deferredQuery = useDeferredValue(query.trim());

  const [draft, setDraft] = useState<Draft | null>(null);

  const where = deferredQuery
    ? {
        accountId,
        OR: [
          { name: { contains: deferredQuery, mode: 'insensitive' as const } },
          { relationshipTag: { contains: deferredQuery, mode: 'insensitive' as const } },
          ...(seesNumbers ? [{ e164: { contains: normalisePhone(deferredQuery) } }] : []),
        ],
      }
    : { accountId };

  const contactsQ = crud.contact.useFindMany({
    where,
    // Named people first and alphabetically; the unnamed ones fall to the bottom
    // together, which is also the list you want to work through and name.
    orderBy: [{ name: 'asc' }, { e164: 'asc' }],
    take: 200,
  });
  const contacts = (contactsQ.data as ContactRow[] | undefined) ?? [];

  const createM = crud.contact.useCreate();
  const updateM = crud.contact.useUpdate();
  const deleteM = crud.contact.useDelete();
  const busy = createM.isPending || updateM.isPending || deleteM.isPending;

  // The number is unique per account, so a duplicate is the likeliest mistake on this
  // screen — saving the same caller twice. Asked as you type rather than on submit, so
  // it reads as "you already know this person" instead of a rejected form. The unique
  // index stays the truth; this only makes the common case explainable.
  const draftPhone = draft ? normalisePhone(draft.e164) : '';
  const phoneValid = E164.test(draftPhone);
  const clashQ = crud.contact.useFindFirst(
    {
      where: {
        accountId,
        e164: draftPhone,
        ...(draft?.id ? { id: { not: draft.id } } : {}),
      },
      select: { id: true, name: true, e164: true },
    },
    { enabled: !!draft && phoneValid },
  );
  const clash = (clashQ.data as { name: string | null; e164: string } | null | undefined) ?? null;

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft) return;
    const e164 = draftPhone;
    if (!phoneValid) {
      toast.error(t('contacts.badPhone'));
      return;
    }
    if (clash) {
      toast.error(t('contacts.duplicate', { name: clash.name || clash.e164 }));
      return;
    }

    // A message must carry an expiry — compute it from the chosen duration; an empty
    // message clears both. Saving (re)starts the clock from now.
    const callMessage = draft.callMessage.trim() || null;
    let callMessageExpiresAt: Date | null = null;
    if (callMessage) {
      const amount = Number(draft.durationAmount);
      const max = DURATION_MAX[draft.durationUnit];
      if (!Number.isInteger(amount) || amount < 1 || amount > max) {
        toast.error(t('contacts.durationRequired'));
        return;
      }
      const ms = draft.durationUnit === 'hours' ? amount * 3_600_000 : amount * 86_400_000;
      callMessageExpiresAt = new Date(Date.now() + ms);
    }

    const data = {
      e164,
      name: draft.name.trim() || null,
      relationshipTag: draft.relationshipTag.trim() || null,
      notes: draft.notes.trim() || null,
      callMessage,
      callMessageExpiresAt,
    };

    try {
      if (draft.id) await updateM.mutateAsync({ where: { id: draft.id }, data });
      else await createM.mutateAsync({ data: { accountId, ...data } });
      setDraft(null);
      toast.success(t('contacts.saved'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  /** Create a batch of picked/parsed contacts. Each gets a normalised +E.164
   *  number; entries without a valid international number are skipped (we don't
   *  guess a country), and duplicates throw on the unique index → caught → skipped.
   *  One toast with the tally at the end. */
  async function bulkCreate(entries: Array<{ name: string | null; tel: string }>) {
    let added = 0;
    let skipped = 0;
    for (const entry of entries) {
      const e164 = normalisePhone(entry.tel);
      if (!E164.test(e164)) {
        skipped++;
        continue;
      }
      try {
        await createM.mutateAsync({ data: { accountId, e164, name: entry.name } });
        added++;
      } catch {
        skipped++; // duplicate number or a rejected write — skip, keep going
      }
    }
    toast.success(t('contacts.imported', { added, skipped }));
  }

  /** Import from the device address book via the Contact Picker (Android only). */
  async function importFromPhone() {
    const api = getContactsManager();
    if (!api) return;
    let picked: PickedContact[];
    try {
      picked = await api.select(['name', 'tel'], { multiple: true });
    } catch {
      return; // cancelled or blocked
    }
    if (!picked.length) return;
    await bulkCreate(
      picked.map((p) => ({
        tel: p.tel?.find((tel) => tel.trim()) ?? '',
        name: p.name?.find((n) => n.trim())?.trim() || null,
      })),
    );
  }

  /** Import from a vCard (.vcf) file — the cross-platform path (works on iOS, where
   *  the Contact Picker does not). The owner exports their contacts to a .vcf from
   *  the phone's Contacts app and uploads it here. */
  async function importFromFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    try {
      const entries = parseVcards(await file.text());
      if (!entries.length) {
        toast.error(t('contacts.importEmpty'));
        return;
      }
      await bulkCreate(entries);
    } catch {
      toast.error(t('contacts.importFailed'));
    }
  }

  async function onDelete(c: ContactRow) {
    if (!window.confirm(t('contacts.confirmDelete', { who: c.name || c.e164 }))) return;
    try {
      await deleteM.mutateAsync({ where: { id: c.id } });
      setDraft(null);
      toast.success(t('contacts.deleted'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  return (
    <div className="h-full overflow-y-auto px-4 pt-6 pb-24">
      <div className="mx-auto max-w-md space-y-5">
        <div className="flex items-center gap-2">
          <h1 className="flex-1 text-xl font-semibold">{t('nav.contacts')}</h1>
          {mayEdit ? (
            <button
              type="button"
              onClick={() => setDraft(draft && !draft.id ? null : { ...EMPTY })}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {draft && !draft.id ? (
                <X className="h-4 w-4" aria-hidden />
              ) : (
                <Plus className="h-4 w-4" aria-hidden />
              )}
              {draft && !draft.id ? t('action.cancel') : t('contacts.add')}
            </button>
          ) : null}
        </div>

        {mayEdit ? (
          <div className="flex flex-wrap gap-2">
            {/* Cross-platform import — works on iOS too (there is no Contact Picker
                there). The owner exports a .vcf from the phone and uploads it. */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <Upload className="h-4 w-4" aria-hidden />
              {t('contacts.importVcf')}
            </button>
            {/* Android only — the Contact Picker API. Hidden where unsupported. */}
            {contactsApi ? (
              <button
                type="button"
                onClick={importFromPhone}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <Smartphone className="h-4 w-4" aria-hidden />
                {t('contacts.importPhone')}
              </button>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              accept=".vcf,text/vcard,text/x-vcard"
              hidden
              onChange={importFromFile}
            />
          </div>
        ) : null}

        {!mayEdit ? <p className="text-sm text-muted-foreground">{t('contacts.readOnly')}</p> : null}

        {draft ? (
          <form onSubmit={onSave} className="space-y-3 rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-medium">
              {draft.id ? t('contacts.editHeading') : t('contacts.addHeading')}
            </h2>
            <label className="block">
              <span className="text-xs text-muted-foreground">{t('contacts.phone')}</span>
              <input
                type="tel"
                required
                inputMode="tel"
                autoComplete="tel"
                value={draft.e164}
                onChange={(e) => setDraft({ ...draft, e164: e.target.value })}
                placeholder="+359888123456"
                className="input mt-1 w-full"
              />
              <span
                className={
                  clash ? 'mt-1 block text-xs text-destructive' : 'mt-1 block text-xs text-muted-foreground'
                }
              >
                {clash
                  ? t('contacts.duplicate', { name: clash.name || clash.e164 })
                  : t('contacts.phoneHint')}
              </span>
            </label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('contacts.name')}
              className="input w-full"
            />
            <input
              type="text"
              value={draft.relationshipTag}
              onChange={(e) => setDraft({ ...draft, relationshipTag: e.target.value })}
              placeholder={t('contacts.relationshipPlaceholder')}
              className="input w-full"
            />
            <textarea
              rows={2}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder={t('contacts.notePlaceholder')}
              className="input w-full resize-y"
            />

            <label className="block">
              <span className="text-xs text-muted-foreground">{t('contacts.callMessage')}</span>
              <textarea
                rows={2}
                value={draft.callMessage}
                onChange={(e) => setDraft({ ...draft, callMessage: e.target.value })}
                placeholder={t('contacts.callMessagePlaceholder')}
                className="input mt-1 w-full resize-y"
              />
            </label>
            {/* A message must carry an expiry — the duration row appears only when
                there is text, and saving starts the clock from now. */}
            {draft.callMessage.trim() ? (
              <label className="block">
                <span className="text-xs text-muted-foreground">{t('contacts.duration')}</span>
                <div className="mt-1 flex gap-2">
                  <input
                    type="number"
                    min={1}
                    max={DURATION_MAX[draft.durationUnit]}
                    value={draft.durationAmount}
                    onChange={(e) => setDraft({ ...draft, durationAmount: e.target.value })}
                    className="input w-24"
                    aria-label={t('contacts.duration')}
                  />
                  <select
                    value={draft.durationUnit}
                    onChange={(e) => {
                      const unit = e.target.value as DurationUnit;
                      // Clamp the amount into the new unit's range.
                      const amount = Math.min(Number(draft.durationAmount) || 1, DURATION_MAX[unit]);
                      setDraft({ ...draft, durationUnit: unit, durationAmount: String(amount) });
                    }}
                    className="input flex-1"
                    aria-label={t('contacts.duration')}
                  >
                    <option value="hours">{t('contacts.durationHours')}</option>
                    <option value="days">{t('contacts.durationDays')}</option>
                  </select>
                </div>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {t('contacts.durationHint')}
                </span>
              </label>
            ) : null}

            <div className="flex gap-2">
              <button type="submit" disabled={busy || !!clash} className="btn-primary flex-1">
                {busy ? t('contacts.saving') : t('action.save')}
              </button>
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="rounded-md border border-border px-4 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {t('action.cancel')}
              </button>
            </div>
          </form>
        ) : null}

        <label className="relative block">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={seesNumbers ? t('contacts.search') : t('contacts.searchNoNumbers')}
            className="input w-full pl-9"
            aria-label={seesNumbers ? t('contacts.search') : t('contacts.searchNoNumbers')}
          />
        </label>

        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {deferredQuery ? t('contacts.noMatches') : t('contacts.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {contacts.map((c) => (
              <li key={c.id} className="flex items-start gap-2 px-4 py-3">
                <button
                  type="button"
                  // Read-only for a viewer: the row still opens nothing rather than
                  // opening a form whose Save would be refused.
                  onClick={
                    mayEdit
                      ? () =>
                          setDraft({
                            id: c.id,
                            e164: c.e164,
                            name: c.name ?? '',
                            relationshipTag: c.relationshipTag ?? '',
                            notes: c.notes ?? '',
                            // The original duration isn't stored (only the computed
                            // expiry), so the picker resets to a default; the current
                            // expiry is shown below the field. Saving restarts it.
                            callMessage: c.callMessage ?? '',
                            durationAmount: '1',
                            durationUnit: 'hours',
                          })
                      : undefined
                  }
                  disabled={!mayEdit}
                  className="min-w-0 flex-1 text-left disabled:cursor-default"
                >
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {c.name || t('contacts.unnamed')}
                    </span>
                    {c.relationshipTag ? (
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {c.relationshipTag}
                      </span>
                    ) : null}
                  </span>
                  {/* Withheld from a VIEWER by a field rule, which returns null. Say so
                      rather than leaving a phone icon with nothing beside it — a blank
                      reads as a contact with no number, which is not what happened. */}
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Phone className="h-3 w-3 shrink-0" aria-hidden />
                    {c.e164 ?? <em className="not-italic opacity-70">{t('contacts.numberHidden')}</em>}
                  </span>
                  {c.notes ? (
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {c.notes}
                    </span>
                  ) : null}
                </button>
                {mayEdit ? (
                  <button
                    type="button"
                    onClick={() => onDelete(c)}
                    disabled={busy}
                    className="shrink-0 rounded-md border border-border p-2 text-muted-foreground hover:bg-muted hover:text-destructive"
                    aria-label={t('contacts.delete')}
                    title={t('contacts.delete')}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
